// src/userFlow/SignUpPage.js
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import API_BASE from '../apiBase';
import QaAlert from '../components/QaAlert';
import RequiredMark from '../components/RequiredMark';

export default function SignUpPage() {
  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    confirm: '',
  });
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const navigate = useNavigate();

  const emailRule = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const pwdRule = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,15}$/;

  function onChange(e) {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  }

  function validate() {
    const e = {};
    if (!form.first_name.trim()) e.first_name = 'First name is required.';
    if (!form.last_name.trim()) e.last_name = 'Last name is required.';
    if (!form.email.trim()) e.email = 'Email is required.';
    else if (!emailRule.test(form.email)) e.email = 'Enter a valid email.';
    if (!form.password) e.password = 'Password is required.';
    else if (!pwdRule.test(form.password))
      e.password = '8–15 chars, with upper, lower, number & special.';
    if (!form.confirm) e.confirm = 'Please confirm your password.';
    else if (form.confirm !== form.password) e.confirm = 'Passwords do not match.';
    return e;
  }

  async function onSubmit(e) {
    e.preventDefault();
    setServerError('');
    const eMap = validate();
    setErrors(eMap);
    if (Object.keys(eMap).length) return;

    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/auth/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          first_name: form.first_name.trim(),
          last_name: form.last_name.trim(),
          email: form.email.trim().toLowerCase(),
          password: form.password,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setServerError(data.error || 'Failed to create account.');
        return;
      }
      navigate('/signin', { replace: true, state: { email: form.email.trim().toLowerCase() } });
    } catch {
      setServerError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-between" data-qa="signup-page">
      <div className="flex-grow flex items-center justify-center px-4">
        <div className="bg-white shadow-lg rounded-xl w-full max-w-md p-8" data-qa="signup-card">
          <h2 className="text-3xl font-bold bg-gradient-to-r from-orange-500 to-red-500 bg-clip-text text-transparent text-center">
            Create your account
          </h2>
          <p className="text-gray-600 text-center mt-2 mb-4">
            Join SodaSips to start sipping & saving.
          </p>

          {/* Always-mounted alert region (stable selector) */}
          <QaAlert id="signup-alert" dataQa="qa-signup-alert" message={serverError} variant="error" />

          <form className="space-y-5" onSubmit={onSubmit} noValidate data-qa="qa-signup-form">
            {/* First Name */}
            <div data-qa="qa-signup-first-name-field">
              <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="signup-first-name">
                First Name <RequiredMark />
              </label>
              <input
                id="signup-first-name"
                data-qa="qa-signup-first-name"
                name="first_name"
                type="text"
                value={form.first_name}
                onChange={onChange}
                className={`w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                  errors.first_name ? 'border-red-400' : 'border-gray-300'
                }`}
                aria-describedby="signup-error-first-name"
                aria-invalid={Boolean(errors.first_name)}
                required
                aria-required="true"
              />
              <p
                id="signup-error-first-name"
                data-qa="qa-signup-error-first-name"
                className={errors.first_name ? 'text-xs text-red-500 mt-1' : 'sr-only'}
              >
                {errors.first_name || ''}
              </p>
            </div>

            {/* Last Name */}
            <div data-qa="qa-signup-last-name-field">
              <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="signup-last-name">
                Last Name <RequiredMark />
              </label>
              <input
                id="signup-last-name"
                data-qa="qa-signup-last-name"
                name="last_name"
                type="text"
                value={form.last_name}
                onChange={onChange}
                className={`w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                  errors.last_name ? 'border-red-400' : 'border-gray-300'
                }`}
                aria-describedby="signup-error-last-name"
                aria-invalid={Boolean(errors.last_name)}
                required
                aria-required="true"
              />
              <p
                id="signup-error-last-name"
                data-qa="qa-signup-error-last-name"
                className={errors.last_name ? 'text-xs text-red-500 mt-1' : 'sr-only'}
              >
                {errors.last_name || ''}
              </p>
            </div>

            {/* Email */}
            <div data-qa="qa-signup-email-field">
              <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="signup-email">
                Email Address <RequiredMark />
              </label>
              <input
                id="signup-email"
                data-qa="qa-signup-email"
                name="email"
                type="email"
                value={form.email}
                onChange={onChange}
                placeholder="you@example.com"
                className={`w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                  errors.email ? 'border-red-400' : 'border-gray-300'
                }`}
                aria-describedby="signup-error-email"
                aria-invalid={Boolean(errors.email)}
                required
                aria-required="true"
              />
              <p
                id="signup-error-email"
                data-qa="qa-signup-error-email"
                className={errors.email ? 'text-xs text-red-500 mt-1' : 'sr-only'}
              >
                {errors.email || ''}
              </p>
            </div>

            {/* Password */}
            <div data-qa="qa-signup-password-field">
              <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="signup-password">
                Password <RequiredMark />
              </label>
              <div className="relative">
                <input
                  id="signup-password"
                  data-qa="qa-signup-password"
                  name="password"
                  type={showPwd ? 'text' : 'password'}
                  value={form.password}
                  onChange={onChange}
                  placeholder="••••••••"
                  className={`w-full border rounded-lg px-3 py-2 pr-16 focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                    errors.password ? 'border-red-400' : 'border-gray-300'
                  }`}
                  aria-describedby="signup-error-password pwd-help"
                  aria-invalid={Boolean(errors.password)}
                  required
                  aria-required="true"
                />
                <button
                  type="button"
                  data-qa="qa-signup-toggle-password"
                  onClick={() => setShowPwd((s) => !s)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-sm text-gray-500 px-2 py-1"
                >
                  {showPwd ? 'Hide' : 'Show'}
                </button>
              </div>
              <p id="pwd-help" className="text-xs text-gray-500 mt-1">
                8–15 chars, include uppercase, lowercase, number, and special character.
              </p>
              <p
                id="signup-error-password"
                data-qa="qa-signup-error-password"
                className={errors.password ? 'text-xs text-red-500 mt-1' : 'sr-only'}
              >
                {errors.password || ''}
              </p>
            </div>

            {/* Confirm Password */}
            <div data-qa="qa-signup-confirm-field">
              <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="signup-confirm">
                Confirm Password <RequiredMark />
              </label>
              <div className="relative">
                <input
                  id="signup-confirm"
                  data-qa="qa-signup-confirm"
                  name="confirm"
                  type={showConfirm ? 'text' : 'password'}
                  value={form.confirm}
                  onChange={onChange}
                  placeholder="••••••••"
                  className={`w-full border rounded-lg px-3 py-2 pr-16 focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                    errors.confirm ? 'border-red-400' : 'border-gray-300'
                  }`}
                  aria-describedby="signup-error-confirm"
                  aria-invalid={Boolean(errors.confirm)}
                  required
                  aria-required="true"
                />
                <button
                  type="button"
                  data-qa="qa-signup-toggle-confirm"
                  onClick={() => setShowConfirm((s) => !s)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-sm text-gray-500 px-2 py-1"
                >
                  {showConfirm ? 'Hide' : 'Show'}
                </button>
              </div>
              <p
                id="signup-error-confirm"
                data-qa="qa-signup-error-confirm"
                className={errors.confirm ? 'text-xs text-red-500 mt-1' : 'sr-only'}
              >
                {errors.confirm || ''}
              </p>
            </div>

            <button
              type="submit"
              data-qa="qa-signup-submit"
              disabled={loading}
              className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold py-3 px-4 rounded-lg transition-colors duration-200 disabled:opacity-60"
            >
              {loading ? 'Creating…' : 'Sign Up'}
            </button>
          </form>

          <p className="text-center text-sm text-gray-600 mt-6" data-qa="signup-links">
            Already have an account?{' '}
            <Link to="/signin" className="text-orange-500 hover:underline" data-qa="qa-signup-to-signin">
              Sign In
            </Link>
          </p>
        </div>
      </div>

      <footer className="bg-white border-t border-gray-200 py-4">
        <div className="text-center text-sm text-gray-500">
          <Link to="/about" className="hover:underline mr-4">About Us</Link>
          © 2025 SodaSips. All rights reserved.
        </div>
      </footer>
    </div>
  );
}
